import { FC, useCallback, useContext, useEffect, useState } from 'react';
// import classNames from 'classnames';
import { Switch, Route, useHistory, useRouteMatch, useParams } from 'react-router-dom';
import { useWxWork } from '@cloud/wxwork-workbench';
import PageLayout from '@/layouts/page';
import ScreenContext from '@/contexts/screen';
import Button from '@/components/Button';
import Toast from '@/components/Toast';
import Tooltip from '@/components/Tooltip';
import Tag, { TagProps } from '@/components/Tag';
import Table, { ColumnType } from '@/components/Table';
import WechatMessage, { MessageType } from '@/components/WechatMessage';
import { ReactComponent as IHelp } from '@/assets/icons/help.svg';
import { go, goBack } from '@/utils';
import { smallSizeWidth } from '@/constants';
import { SOP, SOPSendType, SOPStatus, SOPType, COMPLETE_MARKER } from '../constants';
import CustomerListPage from './customer-list';
import { markDailyMessageSend, markSOPTaskFinish, querySOPDetail, sendWeeklyMessage } from './service';
import styles from './index.less';


export interface SOPDetailProps {
  onBack?: () => void;
}

const SOPDetailPage: FC<SOPDetailProps> = ({ onBack }) => {
  const history = useHistory();
  let { path, url } = useRouteMatch();
  const { id } = useParams<{ id?: string }>();
  const { type } = useParams<{ type?: string }>();
  const signature = useWxWork();
  const { width: screenWidth } = useContext(ScreenContext);
  const [data, setData] = useState<SOP>();
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!id) {
      onBack();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetch = useCallback(async () => {
    const res = await querySOPDetail(Number(id), signature);
    setData(res as SOP);
  }, [id, signature]);

  useEffect(() => {
    if (signature.corpId && signature.userId) {
      fetch();
    }
  }, [signature, fetch]);

  const handleCheckCustomerList = useCallback(() => {
    go(history, `${history.location.pathname}/customer-list`);
  }, [history]);

  const initContent = (messageContent) => {
    const _content = {
      text:{
        content:"",    // 文本内容
      },
      attachments: []
    }
    messageContent.forEach(e => {
      const NEW_LINE = 'þ_enter_þ';
      const { type, content } = e;
      const replaceText = content.text && content.text.replace(new RegExp(NEW_LINE,"gm"),'\n')
      if (type === MessageType.text) {
        _content.text.content = replaceText;
      } else if (type === MessageType.image) {
        _content.attachments.push({
          msgtype: "image",
          image: {
            mediaid: "",
            imgUrl: content.picUrl || ''
          }
        });
      } else if (type === MessageType.link) {
        _content.attachments.push({
          msgtype: "link",
          link: {
            title: content.title || '',
            imgUrl: "",
            desc: "",
            url: ""
          }
        });
      }
    });
    return _content
  }

  const handleQuickSend = useCallback(() => {
    const funcName = {
      [SOPType.CUSTOMER_SOP]: 'shareToExternalContact',
      [SOPType.CHAT_SOP]: 'shareToExternalChat'
    }[data?.entityType];
    let content: any = {};
    if (!(navigator.userAgent.indexOf('Mac') >= 0 && navigator.userAgent.indexOf('iPhone') < 0)) {
      content = initContent(data.messageContent);
    }
    wx?.invoke(
      funcName,
      content,
      (res: any) => {
        if (res?.err_msg === `${funcName}:ok`) {
          markDailyMessageSend(Number(id), signature).then(result => {
            if (!result) {
              Toast.success('发送失败');
            }
          }).finally(() => fetch());
        } else {
          if (res.err_msg === 'shareToExternalChat:permission denied') {
            Toast.error('Mac 端暂不支持客户群群发');
          } else if (res.err_msg === `${funcName}:cancel`) {
            console.log('[群发取消]');
          }
          else if(res.err_msg !== 'shareToExternalChat:fail_empty parameters'){
            Toast.error('未知错误，无法打开群发助手');
            console.log('[打开群发助手错误]：', res?.err_msg);
          }
        }
      }
    );
  }, [data, id, signature, fetch]);

  const handleOneKeySend = useCallback(async () => {
    try {
      setIsLoading(true);
      const result = await sendWeeklyMessage(Number(id), signature);
      if (result) {
        Toast.success('发送成功');
      } else {
        Toast.error('发送失败');
      }
    } finally {
      setIsLoading(false);
      fetch();
    }
  }, [id, signature, fetch]);

  const handleFinish = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await markSOPTaskFinish(Number(id), signature);
      if (result) {
        Toast.success('标记成功');
      } else {
        Toast.error('发送失败');
      }
    } finally {
      setIsLoading(false);
      fetch();
    }
  }, [id, signature, fetch]);

  const sopSendTypeMap = {
    [SOPSendType.DAY]: {
      text: '日',
      btnText: '快捷',
      func: handleQuickSend,
      help: (
        <ol className={styles.helpTips}>
          <li>快捷发送是通过企业微信官方的群发助手的能力对目标客户/群发送消息。</li>
          <li>每天发送最多可选择200个客户/群，每次发送消息前请先确认目标客户/群，避免漏选或错选。</li>
        </ol>
      )
    },
    [SOPSendType.WEEK]: {
      text: '周',
      btnText: '一键',
      func: handleOneKeySend,
      help: (
        <ol className={styles.helpTips}>
          <li>一键发送是通过企业官方的群发消息功能对目标客户/群发送消息，所以会消耗企业微信官方的群发消息数，每个客户/群每个月仅可接受4次。</li>
          <li>一键发送会将消息发送【客户联系】中，经再次确认后，才可发送给客户。</li>
          <li>为了提高效率和价值最大化，尽量在发送的目标客户/群较多的时候使用。</li>
        </ol>
      )
    }
  }

  const {
    sopName,
    scene,
    executeTime,
    sopSendType = SOPSendType.DAY,
    totalAmount,
    entityType,
    messageContent = [],
    executionResult,
    hasSend,
    status
  } = data || {};

  const columns: ColumnType<SOP>[] = [
    {
      title: '任务状态', dataIndex: 'status',width: 100,
      render: v => (
        <Tag
          {...({
            [SOPStatus.PENDING]: { type: 'warning', children: '待完成' } as TagProps,
            [SOPStatus.COMPLETE]: { type: 'success', children: '已完成' } as TagProps,
          })[v]}
        />
      )
    },
    {
      title: '发送人',
      width: 120,
      render: () =>
        <span className={styles.userName}
          dangerouslySetInnerHTML={{
            __html: `<ww-open-data corpid=${signature.corpId} type='userName' openid=${data.userId}></ww-open-data>`
          }}
        />
    },
    {
      title: '实际发送时间', dataIndex: 'sendTime', width: 100,
      render: () => <span>{executionResult.sendTime ? executionResult.sendTime : '--'}</span> },
    {
      title: '发送方式',
      dataIndex: 'sendType',
      render: (v: SOPSendType) => `${sopSendTypeMap[v].btnText}发送`
    },
    { title: '已发送客户数', dataIndex: 'hasSendAmount' },
    ...(sopSendType === SOPSendType.DAY ? [] : [
      { title: '发送成功', dataIndex: 'successAmount', render: v => v ?? '计算中，稍后查看' },
      { title: '发送失败', dataIndex: 'failureAmount', render: v => v ?? '计算中，稍后查看' },
    ])
  ]

  return (
    <Switch>
      <Route path={`${path}/customer-list`}>
        <CustomerListPage onBack={useCallback(() => goBack(history, url), [history, url])} />
      </Route>
      <Route path={path}>
        <PageLayout
          onBack={screenWidth < smallSizeWidth ? undefined : onBack}
          extra={(
            <Button type='link' className={styles.helpButton}
              onClick={() => window.open('https://docs.qq.com/doc/DVUtVT1JzTUZ5b2dY')}
            >发送技巧</Button>
          )}
        >
          <section style={{height:'calc(100vh - 131px)',overflowY:'auto'}}>
            {(executionResult && hasSend) &&
              <section className={styles.result}>
                <div className={styles.title}>
                  <h3>执行结果</h3>
                  <Button type='link' size='small' onClick={handleCheckCustomerList}>查看执行明细</Button>
                </div>
              <Table
                  scroll={{ x: 'max-content' }}
                  columns={columns}
                  rowKey='key'
                  className={styles.table}
                  data={[{ key: 'result', ...executionResult}]}
                />
              </section>
            }
            <section className={styles.info}>
              <h3 className={styles.name}>{sopName}</h3>
              <div className={styles.description}>
              {
                      type === SOPType.CUSTOMER_SOP &&  <div className={styles.item}>
                        <div className={styles.label}>场景：</div>
                          <div className={styles.content}>{scene}</div>
                        </div>
                    }
                <div className={styles.item}>
                  <div className={styles.label}>发送时间：</div>
                  <div className={styles.content}>{executeTime}</div>
                </div>
                <div className={styles.item}>
                  <div className={styles.label}>内容：</div>
                  <div className={styles.content}>
                    {messageContent.map(({ type, content }, i) => {
                      return (
                        <WechatMessage key={i} type={MessageType[type] as any} data={content} />
                      )
                    }
                    )}
                  </div>
                </div>
                <div className={styles.item}>
                  <div className={styles.label}>目标客户{entityType === SOPType.CHAT_SOP ? '群' : ''}：</div>
                  <div className={styles.content}>
                    {totalAmount}
                    <Button type='link' size='small' onClick={handleCheckCustomerList}>
                      查看客户{entityType === SOPType.CHAT_SOP ? '群' : ''}
                    </Button>
                  </div>
                </div>
                {/* <div className={classNames(styles.item, styles.tags)}>
                  <div className={styles.label}>SOP标签：</div>
                  <div className={styles.content}>
                    <span className={styles.tag}>标签内容</span>
                  </div>
                </div> */}
              </div>
              {/* <table className={styles.tip}>
                <th>说明：</th>
                <td>创建SOP任务时对目标客户自动打上唯一的SOP标签，便于通过群发助手发送消息时能快速精准全选目标客户，若没有SOP标签，可以联系管理员在配置SOP页面打开SOP标签功能。</td>
              </table> */}
            </section>
          </section>
          <footer className={styles.footer}>
            {
              hasSend ? (
                <>
                  {
                    status !== SOPStatus.COMPLETE && <Tooltip title={COMPLETE_MARKER}>
                    <Button type='text' className={styles.helpIcon}><IHelp /></Button>
                  </Tooltip>
                  }
                <Button disabled={isLoading || status === SOPStatus.COMPLETE} type='primary' size='large' onClick={handleFinish} className={styles.actionBtn}>
                  标记完成
                </Button>
                </>
              ): (
                <>
                  <Tooltip title={sopSendTypeMap[sopSendType].help}>
                    <Button type='text' className={styles.helpIcon}><IHelp /></Button>
                  </Tooltip>
                  <Button disabled={isLoading} type='primary' size='large' onClick={sopSendTypeMap[sopSendType].func} className={styles.actionBtn}>
                    {isLoading ? '发送中' : `${sopSendTypeMap[sopSendType].btnText}发送`}
                  </Button>
                </>
              )
            }
          </footer>
        </PageLayout>
      </Route>
    </Switch>
  );
}

export default SOPDetailPage;